import {ExportButtonProps, Logo, ParsedDocs, SidebarLayout, TableOfContents,} from '@stoplight/elements-core';
import {Flex, Heading} from '@stoplight/mosaic';
import {NodeType} from '@stoplight/types';
import * as React from 'react';
import {useState} from 'react';
import {Link, Redirect, useLocation} from 'react-router-dom';

import {ServiceNode} from '../../utils/oas/types';
import {computeAPITree, findFirstNodeSlug, isInternal} from './utils';

/////////////////
// CUSTOM
/////////////////
// @ts-ignore
import authentificationMdFile from "../../../docs/authentification.md";
// @ts-ignore
import exemplesMdFile from "../../../docs/exemples.md";
// @ts-ignore
import structureMdFile from "../../../docs/structure.md";
/////////////////
// END - CUSTOM
/////////////////


type SidebarLayoutProps = {
  serviceNode: ServiceNode;
  logo?: string;
  hideTryIt?: boolean;
  hideSchemas?: boolean;
  hideInternal?: boolean;
  hideExport?: boolean;
  exportProps?: ExportButtonProps;
  tryItCredentialsPolicy?: 'omit' | 'include' | 'same-origin';
  tryItCorsProxy?: string;
};

interface ArticleNode {
  data: string;
  type: NodeType.Article;
  [others: string]: any;
}

export const APIWithSidebarLayout: React.FC<SidebarLayoutProps> = ({
  serviceNode,
  logo,
  hideTryIt,
  hideSchemas,
  hideInternal,
  hideExport,
  exportProps,
  tryItCredentialsPolicy,
  tryItCorsProxy,
}) => {

  /////////////////
  // CUSTOM
  /////////////////

  let [authenticationMd, setAuthentificationMd] = useState("");
  fetch(authentificationMdFile).then(res => res.text()).then(setAuthentificationMd);
  let [exemplesMd, setExemplesMd] = useState("");
  fetch(exemplesMdFile).then(res => res.text()).then(setExemplesMd);
  let [structureMd, setStructureMd] = useState("");
  fetch(structureMdFile).then(res => res.text()).then(setStructureMd);

  const articles: ArticleNode[] = [
    {
      data: authenticationMd,
      type: NodeType.Article,
      uri: "/"
    },
    {
      data: structureMd,
      type: NodeType.Article,
      uri: "/structure"
    },
    {
      data: exemplesMd,
      type: NodeType.Article,
      uri: "/exemples"
    }
  ];

  const articleTree = [
    {
      "id": "authentification",
      "title": "Authentification",
      "type": "article",
      "slug": "",
      "meta": ""
    },
    {
      "id": "structure",
      "title": "Structure de l'API",
      "type": "article",
      "slug": "/structure",
      "meta": ""
    },
    {
      "id": "exemples",
      "title": "Exemples",
      "type": "article",
      "slug": "/exemples",
      "meta": ""
    },
    {
      "url": "https://doc.wedof.fr/category/54-api-et-webhooks",
      "title": "Webhooks"
    }
  ];

  /////////////////
  // END - CUSTOM
  /////////////////

  const container = React.useRef<HTMLDivElement>(null);
  const apiTree = React.useMemo(
    () => computeAPITree(serviceNode, { hideSchemas, hideInternal }),
    [serviceNode, hideSchemas, hideInternal],
  );
  const tree = [...articleTree, ...apiTree];

  const location = useLocation();
  const pathname = location.pathname ?? '/';

  const node = articles.find(article => article.uri === pathname) ?? serviceNode.children.find(child => child.uri === pathname);
  if (!node) {
    // Redirect to the first child if node doesn't exist
    const firstSlug = findFirstNodeSlug(tree);

    if (firstSlug) {
      return <Redirect to={firstSlug} />;
    }
  }

  if (hideInternal && node && node.type !== NodeType.Article && isInternal(node)) {
    return <Redirect to="/" />;
  }

  const handleTocClick = () => {
    if (container.current) {
      container.current.scrollIntoView();
    }
  };

  const sidebar = (
    <>
      <Flex ml={4} mb={5} alignItems="center">
        {logo ? (
          <Logo logo={{ url: logo, altText: 'logo' }} />
        ) : (
          serviceNode.data.logo && <Logo logo={serviceNode.data.logo} />
        )}
        <Heading size={4}>{serviceNode.name}</Heading>
      </Flex>
      <Flex flexGrow flexShrink overflowY="auto" direction="col">
        <TableOfContents tree={tree} activeId={pathname} Link={Link} onLinkClick={handleTocClick} />
      </Flex>
    </>
  );

  return (
    <SidebarLayout ref={container} sidebar={sidebar}>
      {node && (
        <ParsedDocs
          key={pathname}
          uri={pathname}
          node={node}
          nodeTitle={node.name}
          layoutOptions={{ hideTryIt: hideTryIt, hideExport: hideExport }}
          location={location}
          allowRouting
          exportProps={exportProps}
          tryItCredentialsPolicy={tryItCredentialsPolicy}
          tryItCorsProxy={tryItCorsProxy}
        />
      )}
    </SidebarLayout>
  );
};
